import { AuthStorage } from "utils/auth-storage";
import moment from "moment";
import {
  FORMULA_DATE_FORMAT,
  getProcurementParentReference,
  firstFilled,
  getGroupingSettings,
  getProcurementRowIdentity,
  parseFormulaDate,
  shiftMomentToNextWorkingDay,
  shiftMomentToPreviousWorkingDay,
} from "./procurementHelpers";
import { ProjectServices } from "apis/Services/ProjectService";

const OVERWRITE_FAMILY_COLUMNS = new Set([
  "issueContract",
  "submittalRequired",
  "submittalApprovalDue",
]);

// Creates a temporary client-side ID for new draft submittals.
function createTempSubmittalId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `new-${crypto.randomUUID()}`;
  }

  return `new-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Extracts referenced column names from a formula string.
function extractFormulaDependencies(formula) {
  return Array.from(new Set(formula.match(/[a-zA-Z_]\w*/g) || []));
}

// Checks whether a row has been edited in the current session.
function isEditedRow(row) {
  return (
    row?.isEdited === true || String(row?.isEdited).toLowerCase() === "true"
  );
}

// Converts schedule critical flags into a consistent boolean check.
function isCriticalScheduleActivity(value) {
  return String(value).trim().toLowerCase() === "true";
}

// Normalizes schedule float values so invalid numbers fall back to zero.
function normalizeScheduleFloatValue(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

// Converts numeric dependencies into the units used by the target formula column.
function resolveFormulaNumericValue({
  targetType,
  dependencyType,
  numericValue,
  dependencySource,
}) {
  const normalizedNumericValue =
    dependencySource === "koncurentSchedule-Total_Float"
      ? numericValue < 0
        ? numericValue
        : 0
      : numericValue;

  if (targetType === "date") {
    return dependencyType === "int"
      ? normalizedNumericValue * 7
      : normalizedNumericValue;
  }

  if (targetType === "float") {
    return dependencyType === "int"
      ? normalizedNumericValue * 7
      : normalizedNumericValue;
  }

  return normalizedNumericValue;
}

// Builds the ordered list of formula-backed procurement columns.
export function buildFormulaColumns(alignedData) {
  return Object.entries(alignedData || {})
    .filter(
      ([columnKey, column]) =>
        Boolean(column?.formula) && columnKey !== "projectedDeliveryDate",
    )
    .sort(
      ([, leftColumn], [, rightColumn]) =>
        parseInt(leftColumn.index || 0, 10) -
        parseInt(rightColumn.index || 0, 10),
    )
    .map(([columnKey, column]) => ({
      key: columnKey,
      formula: column.formula,
      dependencies: extractFormulaDependencies(column.formula),
    }));
}

// Prepares schedule lookup data used when syncing schedule-linked columns.
export function buildScheduleSyncContext(alignedData, schedule) {
  const totalFloatColumnKeys = Object.entries(alignedData || {})
    .filter(([, column]) => column?.source === "koncurentSchedule-Total_Float")
    .map(([columnKey]) => columnKey);
  const activityById = new Map();

  (schedule || []).forEach((scheduleEntry) => {
    if (!scheduleEntry || typeof scheduleEntry !== "object") {
      return;
    }

    const [activityId, activity] = Object.entries(scheduleEntry)[0] || [];
    const normalizedActivityId = firstFilled(activityId);

    if (!normalizedActivityId) {
      return;
    }

    activityById.set(normalizedActivityId, activity || {});
  });

  return {
    totalFloatColumnKeys,
    activityById,
  };
}

// Clears values that are owned by an explicitly removed schedule link.
export function applyScheduleIdClearingPatch(rowPatch = {}) {
  if (
    !Object.prototype.hasOwnProperty.call(rowPatch, "scheduleID") ||
    firstFilled(rowPatch.scheduleID)
  ) {
    return rowPatch;
  }

  return {
    ...rowPatch,
    dateRequiredOnSite: "",
  };
}

// Groups rows into parent-child families so family-wide overwrites can be applied.
function buildFamilyMembership(rows) {
  const rowById = new Map();
  const rootIdCache = new Map();
  const rowIdsByFamilyRootId = new Map();

  (rows || []).forEach((row, index) => {
    const rowId = getProcurementRowIdentity(row, `row-${index}`);

    if (rowId) {
      rowById.set(rowId, row);
    }
  });

  // Walks up parent links to find the root row for a family member.
  const resolveRootId = (rowId) => {
    if (!rowId) {
      return "";
    }

    if (rootIdCache.has(rowId)) {
      return rootIdCache.get(rowId);
    }

    const visited = new Set();
    let currentId = rowId;
    let currentLinkedRow = rowById.get(currentId);

    while (currentLinkedRow) {
      const parentId = getProcurementParentReference(currentLinkedRow);

      if (!parentId || visited.has(parentId) || !rowById.has(parentId)) {
        break;
      }

      visited.add(currentId);
      currentId = parentId;
      currentLinkedRow = rowById.get(currentId);
    }

    rootIdCache.set(rowId, currentId);
    return currentId;
  };

  (rows || []).forEach((row, index) => {
    const rowId = getProcurementRowIdentity(row, `row-${index}`);

    if (!rowId) {
      return;
    }

    const rootId = resolveRootId(rowId);
    if (!rowIdsByFamilyRootId.has(rootId)) {
      rowIdsByFamilyRootId.set(rootId, []);
    }
    rowIdsByFamilyRootId.get(rootId).push(rowId);
  });

  return { rowById, rowIdsByFamilyRootId };
}

// Creates the runtime settings and family metadata needed for recalculation.
export function buildFormulaRuntimeContext(rows, settingsInfo) {
  const groupingSettings = getGroupingSettings(settingsInfo);

  return {
    ...groupingSettings,
    familyMembership: groupingSettings.overwriteCalculations
      ? buildFamilyMembership(rows)
      : null,
  };
}

// Repeatedly evaluates formula columns until dependent values settle.
function evaluateFormulaColumns(
  row,
  formulaColumns,
  alignedData,
  businessCalendarContext,
  skippedColumnKeys = new Set(),
) {
  const nextRow = { ...row };

  if (!formulaColumns.length) {
    return nextRow;
  }

  for (let pass = 0; pass < formulaColumns.length; pass += 1) {
    let hasChanges = false;

    formulaColumns.forEach(({ key, formula }) => {
      if (skippedColumnKeys.has(key)) {
        return;
      }

      const nextValue = evaluateFormulaForRow(
        nextRow,
        key,
        formula,
        alignedData,
        businessCalendarContext,
      );

      if (nextRow[key] !== nextValue) {
        nextRow[key] = nextValue;
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      break;
    }
  }

  return nextRow;
}

// Overwrites selected family fields with the earliest calculated value across the family.
function applyFamilyOverwriteToRows(rows, formulaRuntimeContext) {
  if (!formulaRuntimeContext?.overwriteCalculations) {
    return rows || [];
  }

  const familyMembership = formulaRuntimeContext.familyMembership;
  const rowIdsByFamilyRootId =
    familyMembership?.rowIdsByFamilyRootId || new Map();

  if (!rowIdsByFamilyRootId.size) {
    return rows || [];
  }

  const overwrittenRowsById = new Map(
    (rows || []).map((row, index) => [
      getProcurementRowIdentity(row, `row-${index}`),
      { ...row },
    ]),
  );

  rowIdsByFamilyRootId.forEach((rowIds) => {
    const earliestValues = {};

    OVERWRITE_FAMILY_COLUMNS.forEach((field) => {
      let earliestDate = null;

      rowIds.forEach((rowId) => {
        const currentRow = overwrittenRowsById.get(rowId);
        const parsedDate = parseFormulaDate(currentRow?.[field]);

        if (!parsedDate) {
          return;
        }

        if (!earliestDate || parsedDate.isBefore(earliestDate, "day")) {
          earliestDate = parsedDate;
        }
      });

      earliestValues[field] = earliestDate
        ? earliestDate.format(FORMULA_DATE_FORMAT)
        : "";
    });

    rowIds.forEach((rowId) => {
      const currentRow = overwrittenRowsById.get(rowId);

      if (!currentRow) {
        return;
      }

      OVERWRITE_FAMILY_COLUMNS.forEach((field) => {
        currentRow[field] = earliestValues[field];
      });
    });
  });

  return (rows || []).map(
    (row, index) =>
      overwrittenRowsById.get(getProcurementRowIdentity(row, `row-${index}`)) ||
      row,
  );
}

// Syncs schedule-linked fields and warnings from the latest uploaded schedule.
function synchronizeScheduleFields(
  row,
  scheduleSyncContext,
) {
  if (!row) {
    return row;
  }

  const nextRow = { ...row };
  const scheduleID = firstFilled(nextRow.scheduleID);
  const totalFloatColumnKeys = scheduleSyncContext?.totalFloatColumnKeys || [];

  if (!scheduleID) {
    totalFloatColumnKeys.forEach((columnKey) => {
      nextRow[columnKey] = "";
    });

    nextRow.scheduleSyncWarning = "This item no longer has a schedule ID";
    return nextRow;
  }

  const scheduleActivity = scheduleSyncContext?.activityById?.get(scheduleID);

  if (!scheduleActivity) {
    nextRow.scheduleSyncWarning =
      "The latest uploaded schedule no longer has this ID";
    return nextRow;
  }

  const normalizedFloatValue = normalizeScheduleFloatValue(
    scheduleActivity.totalFloat,
  );

  totalFloatColumnKeys.forEach((columnKey) => {
    nextRow[columnKey] = normalizedFloatValue;
  });

  const scheduleStartDate = parseFormulaDate(scheduleActivity.startDate);
  const currentRequiredOnSiteDate = parseFormulaDate(nextRow.dateRequiredOnSite);

  if (!scheduleStartDate) {
    nextRow.dateRequiredOnSite = "";
    nextRow.scheduleSyncWarning = "";
  } else if (
    !currentRequiredOnSiteDate ||
    !scheduleStartDate.isSame(currentRequiredOnSiteDate, "day")
  ) {
    nextRow.dateRequiredOnSite = scheduleStartDate.format(FORMULA_DATE_FORMAT);
    nextRow.scheduleSyncWarning =
      "The latest uploaded schedule changed this scheduleID's date";
  } else {
    nextRow.scheduleSyncWarning = "";
  }

  if (isEditedRow(nextRow)) {
    nextRow.criticalPathFlag = isCriticalScheduleActivity(
      scheduleActivity.criticalFlag,
    )
      ? "True"
      : "False";
  }

  return nextRow;
}

// Evaluates a single formula against the current row values.
function evaluateFormulaForRow(
  row,
  columnKey,
  formula,
  alignedData,
  businessCalendarContext,
) {
  const columnNames = extractFormulaDependencies(formula);

  if (!columnNames.length) {
    return "";
  }

  const targetType = alignedData?.[columnKey]?.type;
  const dateColumns = columnNames.filter(
    (name) => alignedData?.[name]?.type === "date",
  );
  const isDateFormula = targetType === "date" && dateColumns.length === 1;
  const isTwoDateSubtraction =
    dateColumns.length === 2 &&
    alignedData?.[columnNames[0]]?.type === "date" &&
    alignedData?.[columnNames[1]]?.type === "date" &&
    formula.includes("-");

  let evaluatedFormula = formula;
  let baseDate = null;

  if (isTwoDateSubtraction) {
    const [firstDateKey, secondDateKey] = columnNames;
    const firstDate = parseFormulaDate(row[firstDateKey]);
    const secondDate = parseFormulaDate(row[secondDateKey]);

    if (!firstDate || !secondDate) {
      return "";
    }

    const differenceInDays = firstDate.diff(secondDate, "days");
    evaluatedFormula = evaluatedFormula.replace(
      new RegExp(`${firstDateKey}\\s*-\\s*${secondDateKey}`),
      `${differenceInDays}`,
    );
  }

  for (const dependency of columnNames) {
    if (!Object.prototype.hasOwnProperty.call(row, dependency)) continue;

    const dependencyType = alignedData?.[dependency]?.type;
    const rawValue = row[dependency];

    if (isTwoDateSubtraction && dateColumns.includes(dependency)) {
      continue;
    }

    if (dependencyType === "date") {
      const parsedDate = parseFormulaDate(rawValue);

      if (!parsedDate) {
        return "";
      }

      if (!baseDate) {
        baseDate = parsedDate;
      }

      evaluatedFormula = evaluatedFormula.replace(
        new RegExp(`\\b${dependency}\\b`, "g"),
        "",
      );

      continue;
    }

    if (dependencyType === "int" || dependencyType === "float") {
      const numericValue =
        rawValue === "" || rawValue === null || rawValue === undefined
          ? 0
          : Number(rawValue);

      if (!Number.isFinite(numericValue)) {
        return "";
      }

      const resolvedValue = resolveFormulaNumericValue({
        targetType,
        dependencyType,
        numericValue,
        dependencySource: alignedData?.[dependency]?.source,
      });
      evaluatedFormula = evaluatedFormula.replace(
        new RegExp(`\\b${dependency}\\b`, "g"),
        `(${resolvedValue})`,
      );
    }
  }

  if (/NaN/.test(evaluatedFormula)) {
    return "";
  }

  try {
    const trimmedExpression = evaluatedFormula.trim();
    const result = trimmedExpression
      ? new Function(`return (${trimmedExpression});`)()
      : 0;

    if (!Number.isFinite(result)) {
      return "";
    }

    if (isTwoDateSubtraction) {
      return result;
    }

    if (isDateFormula && baseDate) {
      const normalizedDate = shiftMomentToPreviousWorkingDay(
        baseDate.clone().add(result, "days"),
        businessCalendarContext,
      );

      return normalizedDate
        ? normalizedDate.format(FORMULA_DATE_FORMAT)
        : "";
    }

    return result;
  } catch (error) {
    console.error("Failed to evaluate formula", columnKey, error);
    return "";
  }
}

// Converts week-based duration fields into day counts for calculations.
function getDurationDays(value) {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return numericValue * 7;
}

// Checks whether a projected-delivery milestone has already been consumed by today.
function isPastProjectedMilestone(value, endOfToday) {
  const parsedDate = parseFormulaDate(value);
  return Boolean(parsedDate?.isBefore(endOfToday));
}

// Calculates projected delivery dates and late status from the latest valid milestone.
function calculateProjectedDeliveryFields(row, businessCalendarContext) {
  const endOfToday = moment().endOf("day");
  const todayBase = moment().startOf("day");
  let baseDate = null;
  let remainingDays = 0;

  if (parseFormulaDate(row?.submittalReturned)) {
    baseDate = parseFormulaDate(row.submittalReturned);
    remainingDays = getDurationDays(row?.leadTime);
  } else if (isPastProjectedMilestone(row?.submittalApprovalDue, endOfToday)) {
    baseDate = todayBase.clone();
    remainingDays = getDurationDays(row?.leadTime);
  } else if (parseFormulaDate(row?.submittalIssued)) {
    baseDate = parseFormulaDate(row.submittalIssued);
    remainingDays =
      getDurationDays(row?.approvalTime) + getDurationDays(row?.leadTime);
  } else if (isPastProjectedMilestone(row?.submittalRequired, endOfToday)) {
    baseDate = todayBase.clone();
    remainingDays =
      getDurationDays(row?.approvalTime) + getDurationDays(row?.leadTime);
  } else if (parseFormulaDate(row?.contractIssuedRelease)) {
    baseDate = parseFormulaDate(row.contractIssuedRelease);
    remainingDays =
      getDurationDays(row?.submittalPrepTime) +
      getDurationDays(row?.approvalTime) +
      getDurationDays(row?.leadTime);
  } else if (isPastProjectedMilestone(row?.issueContract, endOfToday)) {
    baseDate = todayBase.clone();
    remainingDays =
      getDurationDays(row?.submittalPrepTime) +
      getDurationDays(row?.approvalTime) +
      getDurationDays(row?.leadTime);
  }

  const projectedDeliveryDate = baseDate
    ? shiftMomentToNextWorkingDay(
        baseDate.clone().add(remainingDays, "days"),
        businessCalendarContext,
      )?.format(FORMULA_DATE_FORMAT) || ""
    : "";
  let projectedDeliveryDateStatus = "";

  if (!firstFilled(row?.actualDeliveryDate) && projectedDeliveryDate) {
    const requiredOnSiteDate = parseFormulaDate(row?.dateRequiredOnSite);
    const projectedDate = parseFormulaDate(projectedDeliveryDate);

    if (
      requiredOnSiteDate &&
      projectedDate &&
      projectedDate.isAfter(requiredOnSiteDate, "day")
    ) {
      projectedDeliveryDateStatus = "late";
    }
  }

  return {
    projectedDeliveryDate,
    projectedDeliveryDateStatus,
  };
}

// Calculates the warning state for the actual delivery date column.
function calculateActualDeliveryDateStatus(row) {
  const requiredOnSiteDate = parseFormulaDate(row?.dateRequiredOnSite);

  if (!requiredOnSiteDate || firstFilled(row?.actualDeliveryDate)) {
    return "";
  }

  const today = moment().startOf("day");
  const daysRemaining = requiredOnSiteDate.startOf("day").diff(today, "day");

  if (daysRemaining < 0) {
    return "late";
  }

  if (daysRemaining < 7) {
    return "pending";
  }

  return "";
}

// Calculates the warning state for the subcontractor committed delivery column.
function calculateSubCommittedDeliveryDateStatus(row) {
  const committedDeliveryDate = parseFormulaDate(row?.subCommittedDeliveryDate);

  if (!committedDeliveryDate || firstFilled(row?.actualDeliveryDate)) {
    return "";
  }

  const today = moment().startOf("day");
  const daysRemaining = committedDeliveryDate.startOf("day").diff(today, "day");

  if (daysRemaining <= 0) {
    return "late";
  }

  if (daysRemaining <= 7) {
    return "pending";
  }

  return "";
}

// Applies derived delivery fields and warning statuses to a row.
function applyDerivedFields(row, businessCalendarContext) {
  const nextRow = { ...row };
  const projectedDeliveryFields = calculateProjectedDeliveryFields(
    nextRow,
    businessCalendarContext,
  );

  nextRow.projectedDeliveryDate = projectedDeliveryFields.projectedDeliveryDate;
  nextRow.projectedDeliveryDateStatus =
    projectedDeliveryFields.projectedDeliveryDateStatus;
  nextRow.actualDeliveryDateStatus = calculateActualDeliveryDateStatus(nextRow);
  nextRow.subCommittedDeliveryDateStatus =
    calculateSubCommittedDeliveryDateStatus(nextRow);

  return nextRow;
}

// Recalculates one procurement row from schedule sync through derived fields.
export function applyFormulaColumnsToRow(
  row,
  formulaColumns,
  alignedData,
  scheduleSyncContext,
  businessCalendarContext,
) {
  if (!row) return row;

  const synchronizedRow = synchronizeScheduleFields(
    row,
    scheduleSyncContext,
  );
  const calculatedRow = evaluateFormulaColumns(
    synchronizedRow,
    formulaColumns,
    alignedData,
    businessCalendarContext,
  );

  return applyDerivedFields(calculatedRow, businessCalendarContext);
}

// Recalculates a full set of procurement rows, including family overwrites when enabled.
export function applyFormulaColumnsToRows(
  rows,
  formulaColumns,
  alignedData,
  scheduleSyncContext,
  settingsInfo,
  businessCalendarContext,
) {
  const synchronizedRows = (rows || []).map((row) =>
    synchronizeScheduleFields(row, scheduleSyncContext),
  );
  const calculatedRows = synchronizedRows.map((row) =>
    evaluateFormulaColumns(
      row,
      formulaColumns,
      alignedData,
      businessCalendarContext,
    ),
  );
  const formulaRuntimeContext = buildFormulaRuntimeContext(
    calculatedRows,
    settingsInfo,
  );
  const overwrittenRows = applyFamilyOverwriteToRows(
    calculatedRows,
    formulaRuntimeContext,
  );
  const postOverwriteCalculatedRows = formulaRuntimeContext.overwriteCalculations
    ? overwrittenRows.map((row) =>
        evaluateFormulaColumns(
          row,
          formulaColumns,
          alignedData,
          businessCalendarContext,
          OVERWRITE_FAMILY_COLUMNS,
        ),
      )
    : overwrittenRows;

  return postOverwriteCalculatedRows.map((row) =>
    applyDerivedFields(row, businessCalendarContext),
  );
}

// Creates a blank draft submittal seeded with the required procurement defaults.
// Calls the API immediately on creation so the row gets a real server-assigned
// entryID before any edits are made — preventing orphaned saves later.
export async function createNewSubmittalDraft(
  alignedData,
  subdivisionRow,
  company_id,
  id,
) {
  const tempEntryId = createTempSubmittalId();
  const divisionTitle = firstFilled(subdivisionRow?.divisionDisplayTitle);
  const subdivisionTitle = firstFilled(subdivisionRow?.displayTitle);
  const nextRow = {};

  Object.keys(alignedData || {}).forEach((key) => {
    nextRow[key] = "";
  });

  const draftRow = {
    ...nextRow,
    entryID: tempEntryId,
    tempId: tempEntryId,
    specSection: subdivisionTitle,
    submittalPrepTime: 2,
    approvalTime: 2,
    leadTime: 2,
    entryOrigin: "Koncurent",
    criticalPathFlag: "False",
    divisionID: divisionTitle,
    divisionId: divisionTitle,
    division: divisionTitle,
    divisionName: divisionTitle,
    div: divisionTitle,
    subdivisionID: subdivisionTitle,
    subdivisionId: subdivisionTitle,
    subdivision: subdivisionTitle,
    subdivisionName: subdivisionTitle,
    subDiv: subdivisionTitle,
    isNew: "true",
  };

  const code = AuthStorage.getItem("token");
  const loginEmail = AuthStorage.getItem("email");

  const reqObj = {
    company: company_id,
    project: id,
    company_id,
    project_id: id,
    code,
    loginEmail,
  };

  try {
    const response = await ProjectServices.procurementLogInsertEntry(
      {
        specSection: subdivisionTitle,
        submittalPrepTime: 2,
        approvalTime: 2,
        leadTime: 2,
        entryOrigin: "Koncurent",
      },
      reqObj,
    );

    const serverEntryId = response?.newEntryID;

    if (serverEntryId) {
      draftRow.entryID = serverEntryId;
      draftRow.tempId = serverEntryId;
      draftRow.id = `submittal:${serverEntryId}`;
      draftRow.isNew = "false";
    }
  } catch (error) {
    console.error("Failed to insert new submittal entry:", error);
  }

  return draftRow;
}
